import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Bars3Icon, XMarkIcon, Cog6ToothIcon,
  ChartBarSquareIcon,
  UsersIcon,
  BanknotesIcon,
  PlusCircleIcon,
  EyeIcon,
  PencilSquareIcon,
  SparklesIcon,
  BeakerIcon,
  InboxArrowDownIcon,
  ArchiveBoxIcon,
  ChatBubbleLeftRightIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  BuildingOfficeIcon,
  ArrowLeftStartOnRectangleIcon,
  ShoppingBagIcon,
  BuildingStorefrontIcon,
  TruckIcon,
  GlobeAltIcon,
  TagIcon,
  CreditCardIcon,
  RocketLaunchIcon,
  SwatchIcon
} from '@heroicons/react/24/outline';
import AdminPanel from './AdminPanel';
import MercadoPagoConfigModal from './MercadoPagoConfigModal';
import ModalPoliticaDescuentos from './ModalPoliticaDescuentos';
import TiendaDisenoModal from './TiendaDisenoModal';
import { getStorefrontUrl } from '../utils/storefront';
import Swal from 'sweetalert2';
import { tieneAccesoModulo } from '../utils/modulosSistema';
import { isDemoUser, abrirTourDemo, solicitarRegistroDemo } from '../utils/demoMode';

function getRolBadgeClass(rol) {
  if (rol === 'superadmin') return 'bg-slate-900 text-white';
  if (rol === 'admin') return 'bg-red-100 text-red-800';
  if (rol === 'gerente') return 'bg-purple-100 text-purple-800';
  return 'bg-blue-100 text-blue-800';
}

function formatRolLabel(rol) {
  return rol ? rol.charAt(0).toUpperCase() + rol.slice(1) : '';
}

const SidebarMobile = ({ currentView, onViewChange, usuario, modulosHabilitados, onLogout, opticaSlug = '' }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [mpModalOpen, setMpModalOpen] = useState(false);
  const [modalPoliticaOpen, setModalPoliticaOpen] = useState(false);
  const [modalDisenoOpen, setModalDisenoOpen] = useState(false);
  const slugActivoOptica = opticaSlug || usuario?.optica_slug || '';
  const [slugDinamico, setSlugDinamico] = useState(slugActivoOptica);

  useEffect(() => {
    if (slugActivoOptica) {
      setSlugDinamico(slugActivoOptica);
      return;
    }
    if (usuario?.optica_id) {
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/auth/mi-optica`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.slug) setSlugDinamico(data.slug);
        })
        .catch(() => {});
    }
  }, [slugActivoOptica, usuario]);

  const [dropdownAnteojosOpen, setDropdownAnteojosOpen] = useState(false);
  const [dropdownEcommerceOpen, setDropdownEcommerceOpen] = useState(false);
  const [dropdownHerramientasOpen, setDropdownHerramientasOpen] = useState(false);
  const [dropdownFinanzasOpen, setDropdownFinanzasOpen] = useState(false);
  const esSuperAdmin = usuario?.rol === 'superadmin';
  const esOperario = usuario?.rol === 'operario';
  const esAdminOGerente = usuario?.rol === 'admin' || usuario?.rol === 'gerente' || esSuperAdmin;
  const hasModulo = (moduloCodigo) => tieneAccesoModulo(modulosHabilitados, moduloCodigo, esSuperAdmin);

  const navItems = [
    { key: 'dashboard', label: 'Dashboard', icon: ChartBarSquareIcon, modulo: 'dashboard' },
    { key: 'clientes', label: 'Clientes', icon: UsersIcon, modulo: 'clientes' },
    { key: 'lista-precios', label: 'Lista de Precios', icon: ClipboardDocumentListIcon, modulo: 'lista_precios' },
    { key: 'nuevo-cliente', label: 'Nuevo Cliente', icon: PlusCircleIcon, modulo: 'nuevo_cliente' }
  ];

  const finanzas = [
    { key: 'finanzas', label: 'Finanzas', icon: BanknotesIcon, modulo: 'finanzas' },
    { key: 'facturacion', label: 'Facturación', icon: DocumentTextIcon, modulo: 'facturacion' },
    { key: 'balance', label: 'Balance', icon: ChartBarIcon, modulo: 'balance' }
  ]

  const navItemsVisibles = navItems.filter((item) => hasModulo(item.modulo));
  const finanzasVisibles = finanzas.filter((item) => hasModulo(item.modulo));

  const anteojosEstados = [
    { key: 'anteojos-todos', label: 'Todos', icon: EyeIcon },
    { key: 'anteojos-tomados', label: 'Tomados', icon: PencilSquareIcon },
    { key: 'anteojos-preparados', label: 'Preparados', icon: SparklesIcon },
    { key: 'anteojos-laboratorio', label: 'En laboratorio', icon: BeakerIcon },
    { key: 'anteojos-para-retirar', label: 'Para retirar', icon: InboxArrowDownIcon },
    { key: 'anteojos-entregados', label: 'Entregados', icon: ArchiveBoxIcon },
  ];
  const anteojosVisibles = hasModulo('estado_anteojos') ? anteojosEstados : [];

  // Sección Tienda Online / E-commerce
  const ecommerceItems = [
    { key: 'vendedor-ecommerce', label: 'Publicaciones y Catálogo', icon: TagIcon, modulo: 'tienda_online', accion: 'view' },
    { key: 'gestion-envios', label: 'Gestión de Envíos', icon: TruckIcon, modulo: 'tienda_online', accion: 'view' },
    { key: 'mercadopago', label: 'Mercado Pago (Cobros)', icon: CreditCardIcon, modulo: 'tienda_online', accion: 'modal_mp' },
    { key: 'politicas-web', label: 'Políticas y Descuentos Web', icon: SparklesIcon, modulo: 'tienda_online', accion: 'modal_politica' },
    { key: 'diseno-tienda', label: 'Diseño y Colores de Tienda', icon: SwatchIcon, modulo: 'tienda_online', accion: 'modal_diseno' },
    { key: 'ver-tienda', label: 'Ver Tienda Online', icon: GlobeAltIcon, modulo: 'tienda_online', accion: 'external' },
  ];
  const ecommerceVisibles = ecommerceItems.filter((item) => hasModulo(item.modulo));

  const herramientasItems = [
    ...(esAdminOGerente ? [{ key: 'estadisticas-ventas', label: 'Estadísticas Ventas', icon: ChartBarIcon, modulo: 'estadisticas_ventas' }] : []),
    { key: 'agregar-cristales', label: 'Cristales', icon: BeakerIcon, modulo: 'herramientas_cristales' },
    { key: 'stock-armazones', label: 'Stock Armazones', icon: EyeIcon, modulo: 'herramientas_stock_armazones' },
    { key: 'mensajeria', label: 'Mensajería', icon: ChatBubbleLeftRightIcon, modulo: 'mensajeria' },
    { key: 'presupuestos', label: 'Presupuestos', icon: DocumentTextIcon, modulo: 'presupuestos' }
  ];
  const herramientasVisibles = herramientasItems.filter((item) => hasModulo(item.modulo));

  const handleNavClick = (viewKey) => {
    onViewChange(viewKey);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: '¿Deseas cerrar sesión?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        setSidebarOpen(false);
        onLogout();
        Swal.fire({
          icon: 'success',
          title: 'Sesión cerrada',
          text: 'Hasta luego',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  };

  return (
    <>
      {/* Botón Hamburguesa - Solo visible en pantallas pequeñas */}
      <div className="md:hidden fixed bottom-6 right-6 z-[1000]">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-4 rounded-full text-white bg-blue-600 shadow-xl hover:bg-blue-700 active:scale-95 transition-all"
        >
          {sidebarOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Overlay - Cierra el sidebar al hacer click */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label="Cerrar menú lateral"
          className="fixed inset-0 bg-black bg-opacity-50 z-[990] md:hidden"
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setSidebarOpen(false);
            }
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-screen w-64 bg-white shadow-lg z-[995] transform transition-transform duration-300 ease-in-out md:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex items-center gap-3">
          <img
            src="/assets/images/H+L.png"
            alt="H+L Optica"
            className="h-12 w-auto"
           />
          <div>
            <p className="text-blue-100 text-sm">Menú de navegación</p>
          </div>
        </div>

        {/* Contenido del Sidebar */}
        <div className="overflow-y-auto h-[calc(100vh-140px)]">
          {/* Banner Modo Demo en Mobile */}
          {usuario && isDemoUser(usuario) && (
            <div className="p-4 bg-gradient-to-br from-indigo-900 to-slate-900 text-white border-b border-indigo-950 space-y-2.5">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-wider text-cyan-300">Modo Demostración</span>
              </div>
              <p className="text-[11px] text-slate-300 leading-snug">
                Explora todos los módulos o activa tu propia óptica con 3 meses gratis.
              </p>
              <div className="grid grid-cols-1 gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setSidebarOpen(false);
                    abrirTourDemo();
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 py-2 px-3 text-xs font-bold text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl"
                >
                  <SparklesIcon className="w-4 h-4 text-cyan-300" />
                  <span>Ver Tour de Módulos</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSidebarOpen(false);
                    solicitarRegistroDemo('activar tu propia óptica con 3 meses gratis');
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 py-2 px-3 text-xs font-black text-white bg-gradient-to-r from-cyan-600 to-indigo-600 rounded-xl shadow-md"
                >
                  <RocketLaunchIcon className="w-4 h-4 text-cyan-200" />
                  <span>Activar mi Óptica (3 Meses Gratis)</span>
                </button>
              </div>
            </div>
          )}

          {/* Sección Principal */}
          <div className="py-4">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Principal
            </p>
            <div className="space-y-1">
              {navItemsVisibles.map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item.key)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    currentView === item.key
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sección Estado de Anteojos */}
          {anteojosVisibles.length > 0 && (
          <div className="py-4 border-t">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Anteojos
            </p>
            <div>
              <button
                onClick={() => setDropdownAnteojosOpen(!dropdownAnteojosOpen)}
                className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                  anteojosVisibles.some(e => e.key === currentView)
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-3">
                  <EyeIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">Estado</span>
                </span>
                <svg
                  className={`h-4 w-4 transition-transform ${dropdownAnteojosOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </button>
              {dropdownAnteojosOpen && (
                <div className="bg-gray-50 space-y-1">
                  {anteojosVisibles.map((estado) => (
                    <button
                      key={estado.key}
                      onClick={() => handleNavClick(estado.key)}
                      className={`w-full text-left px-8 py-2 flex items-center gap-2 text-sm transition-colors ${
                        currentView === estado.key
                          ? 'bg-blue-100 text-blue-600 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <estado.icon className="h-4 w-4 flex-shrink-0" />
                      <span>{estado.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Sección Finanzas */}
          {!esOperario && finanzasVisibles.length > 0 && (
            <div className="py-4 border-t">
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Finanzas
              </p>
              <div>
                <button
                  onClick={() => setDropdownFinanzasOpen(!dropdownFinanzasOpen)}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                    finanzasVisibles.some(f => f.key === currentView)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <BanknotesIcon className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium">Finanzas</span>
                  </span>
                  <svg
                    className={`h-4 w-4 transition-transform ${dropdownFinanzasOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
                {dropdownFinanzasOpen && (
                  <div className="bg-gray-50 space-y-1">
                    {finanzasVisibles.map((item) => (
                      <button
                        key={item.key}
                        onClick={() => handleNavClick(item.key)}
                        className={`w-full text-left px-8 py-2 flex items-center gap-2 text-sm transition-colors ${
                          currentView === item.key
                            ? 'bg-blue-100 text-blue-600 font-semibold'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sección Herramientas */}
          {herramientasVisibles.length > 0 && (
          <div className="py-4 border-t">
            <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Herramientas
            </p>
            <div>
              <button
                onClick={() => setDropdownHerramientasOpen(!dropdownHerramientasOpen)}
                className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                  herramientasVisibles.some(h => h.key === currentView)
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-3">
                  <WrenchScrewdriverIcon className="h-5 w-5 flex-shrink-0" />
                  <span className="font-medium">Herramientas</span>
                </span>
                <svg
                  className={`h-4 w-4 transition-transform ${dropdownHerramientasOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </button>
              {dropdownHerramientasOpen && (
                <div className="bg-gray-50 space-y-1">
                  {herramientasVisibles.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => handleNavClick(item.key)}
                      className={`w-full text-left px-8 py-2 flex items-center gap-2 text-sm transition-colors ${
                        currentView === item.key
                          ? 'bg-blue-100 text-blue-600 font-semibold'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <item.icon className="h-4 w-4 flex-shrink-0" />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Sección Tienda Online / E-commerce */}
          {ecommerceVisibles.length > 0 && (
            <div className="py-4 border-t">
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Tienda Online
              </p>
              <div>
                <button
                  onClick={() => setDropdownEcommerceOpen(!dropdownEcommerceOpen)}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                    ['vendedor-ecommerce', 'gestion-envios'].includes(currentView)
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <BuildingStorefrontIcon className="h-5 w-5 flex-shrink-0 text-cyan-600" />
                    <span className="font-medium">Tienda Online</span>
                  </span>
                  <svg
                    className={`h-4 w-4 transition-transform ${dropdownEcommerceOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
                {dropdownEcommerceOpen && (
                  <div className="bg-gray-50 space-y-1">
                    {ecommerceVisibles.map((item) => (
                      <button
                        key={item.key}
                        onClick={() => {
                          setSidebarOpen(false);
                          if (item.accion === 'view') {
                            handleNavClick(item.key);
                          } else if (item.accion === 'modal_mp') {
                            setMpModalOpen(true);
                          } else if (item.accion === 'modal_politica') {
                            setModalPoliticaOpen(true);
                          } else if (item.accion === 'modal_diseno') {
                            setModalDisenoOpen(true);
                          } else if (item.accion === 'external') {
                            const targetUrl = getStorefrontUrl(slugDinamico || opticaSlug || usuario?.optica_slug);
                            window.open(targetUrl, '_blank');
                          }
                        }}
                        className={`w-full text-left px-8 py-2.5 flex items-center gap-2.5 text-xs transition-colors ${
                          currentView === item.key
                            ? 'bg-blue-100 text-blue-600 font-semibold'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <item.icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Información del Usuario */}
          {usuario && (
            <div className="py-4 border-t">
              <p className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Cuenta
              </p>
              <div className="px-4 py-3 bg-gray-50 rounded-lg mx-2 mb-3">
                <p className="font-semibold text-gray-900">{usuario.nombre_completo}</p>
                <p className="text-xs text-gray-500 truncate">{usuario.email}</p>
                <span className={`inline-block mt-2 px-2 py-1 text-xs rounded font-medium ${getRolBadgeClass(usuario.rol)}`}>
                  {formatRolLabel(usuario.rol)}
                </span>
              </div>

              {/* Opciones del Usuario */}
              <div className="space-y-1">
                {esSuperAdmin && (
                  <button
                    onClick={() => {
                      handleNavClick('superadmin');
                    }}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <BuildingOfficeIcon className="h-5 w-5"/>
                    <span className="font-medium">Gestionar Ópticas</span>
                  </button>
                )}
                {(usuario.rol === 'admin' || usuario.rol === 'superadmin') && (
                  <button
                    onClick={() => {
                      setSidebarOpen(false);
                      setAdminPanelOpen(true);
                    }}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Cog6ToothIcon className="h-5 w-5" />
                    <span className="font-medium">Gestionar Usuarios</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 text-red-600 hover:bg-red-50 transition-colors font-medium"
                >
                  <ArrowLeftStartOnRectangleIcon className="h-5 w-5" />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Admin Panel Modal */}
      {adminPanelOpen && (
        <AdminPanel 
          usuario={usuario} 
          onClose={() => setAdminPanelOpen(false)} 
        />
      )}

      {/* Mercado Pago Modal */}
      <MercadoPagoConfigModal
        isOpen={mpModalOpen}
        onClose={() => setMpModalOpen(false)}
      />

      {/* Políticas de Descuentos Modal */}
      <ModalPoliticaDescuentos
        isOpen={modalPoliticaOpen}
        onClose={() => setModalPoliticaOpen(false)}
      />
      <TiendaDisenoModal
        isOpen={modalDisenoOpen}
        onClose={() => setModalDisenoOpen(false)}
        storefrontUrl={getStorefrontUrl(slugDinamico || opticaSlug || usuario?.optica_slug)}
      />
    </>
  );
};

const usuarioShape = PropTypes.shape({
  nombre_completo: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  rol: PropTypes.string.isRequired,
});

SidebarMobile.propTypes = {
  currentView: PropTypes.string.isRequired,
  onViewChange: PropTypes.func.isRequired,
  usuario: usuarioShape,
  modulosHabilitados: PropTypes.arrayOf(PropTypes.string),
  onLogout: PropTypes.func.isRequired,
  opticaSlug: PropTypes.string,
};

export default SidebarMobile;
